import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getApiUrl } from "../../services/api";
import { createApiError, getApiErrorMessage } from "../../services/apiError";
import { withApiLanguageHeader } from "../../services/languageHeaders";

const BOOK_STATS_TOP_ALL_URL = "/api/BookStats/top-all";

const BOOK_STATS_CACHE_TTL = 5 * 60 * 1000; // 5 минут

const initialState = {
    cache: {},
    lastUpdated: null,
    loading: false,
    error: null
};

const getBookIdentity = (book) => {
    if (!book) return null;

    return (
        book.id ??
        book.bookId ??
        book.bookID ??
        book.Id ??
        book.BookId ??
        book.slug ??
        book.bookSlug ??
        null
    );
};

const normalizeIdentity = (value) => {
    if (value === null || value === undefined) return null;
    return String(value);
};

const cacheContainsBook = (cache, identity) => {
    const normalizedIdentity = normalizeIdentity(identity);

    if (!normalizedIdentity) {
        return false;
    }

    return Object.values(cache).some((value) => {
        if (!Array.isArray(value)) {
            return false;
        }

        return value.some((book) => {
            const bookIdentity = normalizeIdentity(getBookIdentity(book));
            return bookIdentity === normalizedIdentity;
        });
    });
};

export const loadBookStats = createAsyncThunk(
    "bookStats/load",
    async ({ t } = {}, { rejectWithValue }) => {
        try {
            const response = await fetch(getApiUrl(BOOK_STATS_TOP_ALL_URL), {
                method: "GET",
                headers: withApiLanguageHeader(),
                cache: "no-store"
            });

            if (!response.ok) {
                throw await createApiError(response,
                    t?.("bookStats.errors.loadFailedHttp") ||
                    "Помилка під час завантаження статистики. / Ошибка при загрузке статистики.");
            }

            const contentType = response.headers.get("content-type");

            if (!contentType || !contentType.includes("application/json")) {
                return rejectWithValue(
                    t?.("bookStats.errors.invalidJson") ||
                    "Відповідь статистики має некоректний формат. / Ответ статистики имеет некорректный формат."
                );
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(getApiErrorMessage(error,
                t?.("bookStats.errors.loadFailed") ||
                "Не вдалося завантажити статистику книг. / Не удалось загрузить статистику книг."));
        }
    },
    {
        condition: ({ force = false } = {}, { getState }) => {
            if (force) {
                return true;
            }

            const state = getState();
            const bookStats = state.bookStats;

            if (!bookStats) {
                return true;
            }

            if (bookStats.loading) {
                return false;
            }

            if (!bookStats.lastUpdated) {
                return true;
            }

            const lastUpdatedTime = new Date(bookStats.lastUpdated).getTime();

            if (Number.isNaN(lastUpdatedTime)) {
                return true;
            }

            const now = Date.now();

            return now - lastUpdatedTime > BOOK_STATS_CACHE_TTL;
        }
    }
);

const bookStatsSlice = createSlice({
    name: "bookStats",
    initialState,
    reducers: {
        clearBookStatsCache: (state) => {
            state.cache = {};
            state.lastUpdated = null;
            state.error = null;
        },

        invalidateBookStatsCache: (state) => {
            state.lastUpdated = null;
            state.error = null;
        },

        invalidateBookStatsForBook: (state, action) => {
            const identity = action.payload?.bookId ?? action.payload?.id ?? action.payload?.slug ?? action.payload;

            if (!cacheContainsBook(state.cache, identity)) {
                return;
            }

            state.cache = {};
            state.lastUpdated = null;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadBookStats.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loadBookStats.fulfilled, (state, action) => {
                state.loading = false;
                state.cache = action.payload ?? {};
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(loadBookStats.rejected, (state, action) => {
                state.loading = false;

                if (action.meta?.condition) {
                    return;
                }

                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Помилка під час завантаження статистики. / Ошибка при загрузке статистики.";
            });
    }
});

export const {
    clearBookStatsCache,
    invalidateBookStatsCache,
    invalidateBookStatsForBook
} = bookStatsSlice.actions;

export default bookStatsSlice.reducer;
